import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  ReceiptRounded,
  DriveFileRenameOutlineRounded,
  DeleteRounded,
  DownloadRounded,
  SendRounded,
  VisibilityRounded,
  AttachMoneyRounded,
  DescriptionRounded,
  CalendarTodayRounded,
  PersonRounded,
  BusinessRounded,
  MoreVertRounded,
} from "@mui/icons-material";
import NoBills from "../../../assets/no-bills.webp";
import { useDialog } from "../../DialogContext";
import BillPreview from "../Billing/BillPreview";
import EditBillForm from "../Billing/EditBillForm";
import axios from "axios";
import { backend_api } from "../../../static/server";
import { toast } from "react-toastify";
import RecordPaymentFrom from "../Billing/RecordPaymentFrom";
import ActionMenuForm from "../Billing/ActionMenuForm";
import { updateStatus } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { DataGridNoData } from "../../../static/staticdata";
import DataTable from "../../DataTable";
import { generateUniqueMatterName } from "../../../styles/styles";

// Helper function to generate invoice number
const generateInvoiceNumber = (id) => {
  if (id === undefined || id === null) return "";
  return id < 100 ? `INV-${id.toString().padStart(3, "0")}` : `INV-${id}`;
};

// Date formatting
const formatteDateDay = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return dateString;
  }
};

const BillTableForDash = ({
  // Required props
  bills = [],
  loading = false,

  // Optional props with defaults
  height = "100%",
  minHeight = 0,
  emptyStateText = "No invoices available",
  emptyStateImage = NoBills,

  // Pagination props
  pagination = null,
  onPageChange = () => {},

  // Column visibility control
  hiddenColumns = [],
  showColumns = null,

  // Customization
  showActionColumn = true,
  showFilterChips = false,
  filterChips = [],
  showToolbar = true,
  CustomToolbar = null,
  extraBtn = null,

  // Callbacks
  onBillDelete = null,
  onBillEdit = null,
  onBillUpdate = null,
  onRefresh = () => {},

  // External dependencies
  token = "",
  teamMemberLawyerId = "",

  // Custom row actions
  customRowActions = null,

  // Bill operations configuration
  enablePreview = true,
  enableEdit = true,
  enableDelete = true,
  enablePaymentRecording = true,
  enableSubmitApproval = true,
  enableSendInvoice = false,
  enableDownloadPDF = false,
  enableApproveInvoice = false,

  // Styles
  sx = {},
  tableProps = {},
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();

  // State management
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });

  // Status filter options
  const billFilters = [
    { value: "all", label: "All", color: "#9E9E9E" },
    { value: "draft", label: "Draft", color: "#757575" },
    { value: "pending", label: "Pending Approval", color: "#FF9800" },
    { value: "unpaid", label: "Unpaid", color: "#F44336" },
    { value: "paid", label: "Paid", color: "#4CAF50" },
    { value: "overdue", label: "Overdue", color: "#FF5722" },
  ];

  // Bill operations
  const handleOpenPreviewDialog = (bill) => {
    if (enablePreview) {
      showDialog(<BillPreview entryData={bill} />, {
        dialogTitle: bill.invoice_num,
        width: "50rem",
      });
    }
  };

  const handleDownloadBill = (bill) => {
    if (!enableDownloadPDF) return;

    showDialog(
      <BillPreview
        entryData={bill}
        autoDownload={true}
        autoCloseOnDownload={true}
      />,
      {
        dialogTitle: bill.invoice_num,
        width: "50rem",
      }
    );
  };

  const handleEditBill = (bill) => {
    if (enableEdit) {
      showDialog(<EditBillForm editBillData={bill} />, {
        dialogTitle: "Edit Invoice",
        fullScreen: true,
        fullWidth: true,
        width: "100%",
        onSuccess: onRefresh,
      });
    }
  };

  const handleDeleteBill = async (bill) => {
    if (onBillDelete) {
      onBillDelete(bill);
      return;
    }

    if (!enableDelete) return;

    try {
      const res = await axios.delete(
        `${backend_api}firm-case/bill/${bill.id}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res?.status === 204) {
        onRefresh();
        toast.success("Invoice deleted successfully");
      }
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const handleRecordPayment = (bill) => {
    if (enablePaymentRecording) {
      showDialog(<RecordPaymentFrom entryData={bill} onSuccess={onRefresh} />, {
        dialogTitle: "Record Payment",
        width: "40rem",
        onSuccess: onRefresh,
      });
    }
  };

  const handleSubmitForApproval = (bill) => {
    if (enableSubmitApproval) {
      const body = {
        case: bill?.full_data?.case?.id,
        expenses: bill?.full_data?.expenses?.map((exp) => exp.id) || [],
        status: "pending",
        due_date: bill?.full_data?.due_date,
        firm: bill?.full_data?.firm?.id,
      };

      showDialog(
        <ActionMenuForm
          form_type={"submitAproval"}
          body={body}
          BillId={bill.id}
          fetchBills={onRefresh}
        />,
        {
          dialogTitle: "Ask for approval",
        }
      );
    }
  };

  const handleApproveInvoice = (bill) => {
    if (enableApproveInvoice) {
      const body = {
        case: bill?.full_data?.case?.id,
        expenses: bill?.full_data?.expenses?.map((exp) => exp.id) || [],
        status: "unpaid",
        due_date: bill?.full_data?.due_date,
        firm: bill?.full_data?.firm?.id,
      };

      dispatch(
        updateStatus({
          id: bill.id,
          data: body,
          token,
        })
      ).then(() => {
        onRefresh();
        toast.success("Invoice approved successfully");
      });
    }
  };

  // All possible columns definition
  const allColumns = useMemo(
    () => [
      // Action Column
      {
        field: "actions",
        headerName: "Actions",
        width: 80,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Tooltip title="More actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setActionMenu({
                    anchorEl: e.currentTarget,
                    rowData: params.row,
                  });
                }}
              >
                <MoreVertRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={actionMenu.anchorEl}
              open={Boolean(actionMenu.anchorEl)}
              onClose={() => setActionMenu({ anchorEl: null, rowData: null })}
              slotProps={{
                root: { sx: { ".MuiList-root": { padding: 0 } } },
              }}
            >
              {enablePreview && (
                <MenuItem
                  onClick={() => {
                    handleOpenPreviewDialog(actionMenu.rowData);
                    setActionMenu({ anchorEl: null, rowData: null });
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <VisibilityRounded
                    sx={{
                      fontSize: 18,
                      color: "action.active",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Preview
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {enableEdit && (
                <MenuItem
                  onClick={() => {
                    handleEditBill(actionMenu?.rowData?.full_data);
                    setActionMenu({ anchorEl: null, rowData: null });
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <DriveFileRenameOutlineRounded
                    sx={{
                      fontSize: 18,
                      color: "action.active",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Edit
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {actionMenu.rowData?.status === "draft" &&
                enableSubmitApproval && (
                  <MenuItem
                    onClick={() => {
                      handleSubmitForApproval(actionMenu.rowData);
                      setActionMenu({ anchorEl: null, rowData: null });
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <DescriptionRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Submit for Approval
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

              {(actionMenu.rowData?.status === "pending" ||
                actionMenu.rowData?.status === "draft") &&
                enableApproveInvoice && (
                  <MenuItem
                    onClick={() => {
                      handleApproveInvoice(actionMenu.rowData);
                      setActionMenu({ anchorEl: null, rowData: null });
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <AttachMoneyRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Approve Invoice
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

              {actionMenu.rowData?.status === "unpaid" &&
                enablePaymentRecording && (
                  <MenuItem
                    onClick={() => {
                      handleRecordPayment(actionMenu.rowData);
                      setActionMenu({ anchorEl: null, rowData: null });
                    }}
                    sx={{
                      py: 1,
                      px: 2,
                      gap: 1.5,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <ReceiptRounded
                      sx={{
                        fontSize: 18,
                        color: "action.active",
                      }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        Record Payment
                      </Typography>
                    </Box>
                  </MenuItem>
                )}

              {enableSendInvoice && (
                <MenuItem
                  onClick={() => {
                    toast.info("Send invoice functionality");
                    setActionMenu({ anchorEl: null, rowData: null });
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <SendRounded
                    sx={{
                      fontSize: 18,
                      color: "action.active",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Send Invoice
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {enableDownloadPDF && (
                <MenuItem
                  onClick={() => {
                    handleDownloadBill(actionMenu.rowData);
                    setActionMenu({ anchorEl: null, rowData: null });
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <DownloadRounded
                    sx={{
                      fontSize: 18,
                      color: "action.active",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Download PDF
                    </Typography>
                  </Box>
                </MenuItem>
              )}

              {customRowActions &&
                customRowActions(actionMenu.rowData, {
                  closeMenu: () =>
                    setActionMenu({ anchorEl: null, rowData: null }),
                })}

              {enableDelete && actionMenu.rowData?.status !== "paid" && (
                <MenuItem
                  onClick={() => {
                    handleDeleteBill(actionMenu.rowData);
                    setActionMenu({ anchorEl: null, rowData: null });
                  }}
                  sx={{
                    borderTop: "1px solid #e5e5e5",
                    py: 1,
                    px: 2,
                    gap: 1.5,
                    color: "error.main",
                    "&:hover": {
                      bgcolor: "error.main",
                      color: "error.contrastText",
                      "& .MuiSvgIcon-root": {
                        color: "error.contrastText",
                      },
                    },
                  }}
                >
                  <DeleteRounded
                    sx={{
                      fontSize: 18,
                      color: "error.main",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Delete
                    </Typography>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        ),
      },

      // Invoice Number Column
      {
        field: "invoice_num",
        headerName: "Invoice #",
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "primary.main",
              cursor: enablePreview ? "pointer" : "default",
              "&:hover": enablePreview
                ? {
                    textDecoration: "underline",
                  }
                : {},
            }}
            onClick={
              enablePreview
                ? () => handleOpenPreviewDialog(params.row)
                : undefined
            }
          >
            {params.row.invoice_num}
          </Typography>
        ),
      },

      // Client Column
      {
        field: "client",
        headerName: "Client",
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {params.row.client_type === "company" ? (
              <BusinessRounded sx={{ fontSize: "0.9rem", color: "#2e77f5" }} />
            ) : (
              <PersonRounded sx={{ fontSize: "0.9rem", color: "#bf3fe1" }} />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
              {params.row.client}
            </Typography>
          </Box>
        ),
      },

      // Matter Column
      {
        field: "case",
        headerName: "Matter",
        width: 180,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.case}
          </Typography>
        ),
      },

      // Amount Column
      {
        field: "total_amount",
        headerName: "Amount",
        width: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            ${params.row.total_amount?.toFixed(2) || "0.00"}
          </Typography>
        ),
      },

      // Balance Column
      {
        field: "balance",
        headerName: "Balance",
        width: 120,
        renderCell: (params) => {
          const isPaid = params.row.status === "paid";
          const isOverdue = params.row.status === "overdue";

          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: isPaid
                  ? "success.main"
                  : isOverdue
                  ? "error.main"
                  : "warning.main",
                fontWeight: 600,
              }}
            >
              ${params.row.balance?.toFixed(2) || "0.00"}
            </Typography>
          );
        },
      },

      // Due Date Column
      {
        field: "due_date",
        headerName: "Due Date",
        width: 120,
        renderCell: (params) => {
          const isOverdue =
            params.row.status === "overdue" ||
            (params.row.due_date && new Date(params.row.due_date) < new Date());

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayRounded
                sx={{
                  fontSize: "0.9rem",
                  color: isOverdue ? "error.main" : "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85rem",
                  color: isOverdue ? "error.main" : "text.primary",
                  fontWeight: isOverdue ? 600 : 400,
                }}
              >
                {params.row.due_date}
              </Typography>
            </Box>
          );
        },
      },

      // Status Column
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (params) => {
          const statusOption = billFilters.find(
            (opt) => opt.value === params.row.status
          );

          return (
            <Chip
              label={statusOption?.label || params.row.status}
              size="small"
              sx={{
                backgroundColor: `${statusOption?.color || "#9E9E9E"}15`,
                color: statusOption?.color || "#9E9E9E",
                border: `1px solid ${statusOption?.color || "#9E9E9E"}30`,
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          );
        },
      },

      // Issue Date Column
      {
        field: "issue_date",
        headerName: "Issued",
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.issue_date}
          </Typography>
        ),
      },

      // Created Date Column (hidden by default)
      {
        field: "created_date",
        headerName: "Created",
        width: 120,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.created_date || "-"}
          </Typography>
        ),
      },

      // Client Type Column (hidden by default)
      {
        field: "client_type",
        headerName: "Client Type",
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.row.client_type === "company" ? "Company" : "Person"}
            size="small"
            sx={{
              fontSize: "0.7rem",
              backgroundColor:
                params.row.client_type === "company"
                  ? "#2e77f515"
                  : "#bf3fe115",
              color:
                params.row.client_type === "company" ? "#2e77f5" : "#bf3fe1",
            }}
          />
        ),
      },
    ],
    [
      actionMenu,
      token,
      showDialog,
      customRowActions,
      enablePreview,
      enableEdit,
      enableDelete,
      enablePaymentRecording,
      enableSubmitApproval,
      enableSendInvoice,
      enableDownloadPDF,
      enableApproveInvoice,
    ]
  );

  // Filter columns based on props
  const filteredColumns = useMemo(() => {
    let columns = allColumns;

    // If showColumns is provided, only include those columns
    if (showColumns && Array.isArray(showColumns)) {
      columns = columns.filter((col) => showColumns.includes(col.field));
    }

    // Remove hidden columns
    if (hiddenColumns && Array.isArray(hiddenColumns)) {
      columns = columns.filter((col) => !hiddenColumns.includes(col.field));
    }

    // Remove action column if disabled
    if (!showActionColumn) {
      columns = columns.filter((col) => col.field !== "actions");
    }

    return columns;
  }, [allColumns, showColumns, hiddenColumns, showActionColumn]);

  // Transform bills data to rows
  const rows = useMemo(() => {
    if (!Array.isArray(bills)) {
      console.error("bills is not an array:", bills);
      return [];
    }

    return bills.map((item) => {
      const client = item?.case?.case_related_person;
      const clientType =
        client?.contact_type === "company" ? "company" : "person";

      return {
        id: item?.id ?? "-",
        invoice_num: item?.id ? generateInvoiceNumber(item?.id) : "-",
        client: client
          ? `${client?.first_name || ""} ${client?.last_name || ""}`.trim() ||
            client?.company_name ||
            "-"
          : "-",
        client_type: clientType,
        case: item?.case?.id
          ? generateUniqueMatterName(item?.case?.id, item?.case?.case_name)
          : "-",
        total_amount: parseFloat(item?.total_amount) || 0,
        balance: parseFloat(item?.balance) || 0,
        due_date: item?.due_date ? formatteDateDay(item.due_date) : "-",
        issue_date: item?.issue_date ? formatteDateDay(item.issue_date) : "-",
        created_date: item?.created_at ? formatteDateDay(item.created_at) : "-",
        status: item?.status ?? "-",
        full_data: item,
      };
    });
  }, [bills]);

  // Custom no rows overlay
  const CustomNoRowsOverlay = useCallback(
    () => <DataGridNoData imgSrc={emptyStateImage} text={emptyStateText} />,
    [emptyStateImage, emptyStateText]
  );

  const resolvedRowCount =
    pagination?.rowCount ?? pagination?.count ?? rows.length;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minHeight,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <DataTable
        columns={filteredColumns}
        rows={rows}
        extraBtn={extraBtn}
        CustomNoRowsOverlay={CustomNoRowsOverlay}
        CustomToolbar={CustomToolbar}
        height="100%"
        minHeight={0}
        loading={loading}
        page={pagination?.page || 1}
        setPage={(page) => onPageChange(page)}
        pageSize={pagination?.pageSize || 10}
        rowCount={resolvedRowCount}
        count={resolvedRowCount}
        hasNextPage={pagination?.hasNextPage || false}
        hasPreviousPage={pagination?.hasPreviousPage || false}
        filterChips={showFilterChips ? filterChips : []}
        showToolbar={showToolbar}
        {...tableProps}
      />
    </Box>
  );
};

export default React.memo(BillTableForDash);
